import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PerformanceChart from '../../components/PerformanceChart';
import StoriesBar from '../../components/StoriesBar';
import { studentApi } from '../../services/api';
import { AWAITING_ASSIGNMENTS_TEXT, AWAITING_TITLE, awaitingClassesText, studentClassStatus } from '../../utils/classStatus';

const F = { fontFamily: 'Inter' } as const;
const COLORS = ['#3973bf', '#e5599b', '#7a07de', '#86f57c', '#00c6af', '#f87aff'];

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

/**
 * YKS countdown label:
 *   - null date → "Henüz açıklanmadı"
 *   - exam day (today === target) or the following day → "YKS günü!"
 *   - after exam day → "Henüz açıklanmadı" (next year unknown)
 *   - future → N days
 */
function yksCountdownLabel(dateStr: string | null | undefined): { big: string; small: string } {
  if (!dateStr) return { big: '-', small: 'Henüz açıklanmadı' };
  const today = new Date(); today.setUTCHours(0, 0, 0, 0);
  const target = new Date(dateStr); target.setUTCHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0 || diffDays === -1) return { big: 'YKS', small: 'Günü!' };
  if (diffDays < 0) return { big: '-', small: 'Henüz açıklanmadı' };
  return { big: String(diffDays), small: 'Gün' };
}

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  // Bireysel öğrenci (kitap kodu kayıt) → kurum/sınıf bağımlı kartlar gizli
  const isIndividual = !!(user as any)?.is_individual;
  // Kurumlu ama sınıfsız: tam menü, sınıfa bağlı kartlarda açıklama (utils/classStatus.ts).
  const awaiting = studentClassStatus(user) === 'awaiting';
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<any>(null);
  const [books, setBooks] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [chartData, setChartData] = useState<{ label: string; value: number }[]>([]);
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'all'>('week');
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const PERIOD_LABELS: Record<'week' | 'month' | 'all', string> = { week: 'Bu Hafta', month: 'Bu Ay', all: 'Tüm Zamanlar' };
  const [loading, setLoading] = useState(true);
  // Chart domain from backend so the axis is pinned to the correct calendar window
  const [chartDomain, setChartDomain] = useState<{ min?: number; max?: number }>({});

  const loadDashboard = () => {
    Promise.all([
      studentApi.dashboard(),
      studentApi.appointments(),
    ])
      .then(([dashRes, apptRes]) => {
        setDashboard(dashRes.data);
        setBooks(dashRes.data?.library ?? []);
        setAppointments(apptRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chart re-fetches when period changes — also picks up domainMin/domainMax
  useEffect(() => {
    studentApi.performance(chartPeriod)
      .then((res) => {
        const d = res.data;
        setChartData(d?.chartData || []);
        if (d?.domainMin && d?.domainMax) {
          setChartDomain({ min: d.domainMin, max: d.domainMax });
        } else {
          setChartDomain({});
        }
      })
      .catch(console.error);
  }, [chartPeriod]);

  const assignments: any[] = dashboard?.assignments || [];
  const classes: any[] = dashboard?.classes || [];

  // H-2: home-page progress comes from assigned books, not from goals.
  // totalBooks — every book that appears in the student's library.
  // completedBooks — books whose tests are all done (test_count_total > 0 and
  // completed === total). A book with zero tests can't be "completed".
  // overallBookPct — aggregate test completion across all assigned books,
  // weighted by test count so long books don't get drowned by short ones.
  const totalBooks = books.length;
  const completedBooks = books.filter(
    (b: any) => (b.test_count_total ?? 0) > 0 && b.test_count_completed >= b.test_count_total,
  ).length;
  const { sumCompleted, sumTotal } = books.reduce(
    (acc: { sumCompleted: number; sumTotal: number }, b: any) => ({
      sumCompleted: acc.sumCompleted + (b.test_count_completed ?? 0),
      sumTotal: acc.sumTotal + (b.test_count_total ?? 0),
    }),
    { sumCompleted: 0, sumTotal: 0 },
  );
  const overallBookPct = sumTotal > 0 ? Math.round((sumCompleted / sumTotal) * 100) : 0;

  // Pick the nearest upcoming appointment (not just the newest row), falling
  // back to the most recent past one so the card isn't blank when everything
  // already happened. Mirrors the ordering the Randevularım page implies.
  const APPT_TYPE_LABEL: Record<string, string> = {
    advisor: 'Veli Toplantısı',
    teacher: 'Öğretmen Görüşmesi',
    parent: 'Veli Görüşmesi',
    student: 'Öğrenci Görüşmesi',
  };
  const FMT_LABEL: Record<string, string> = { online: 'Online', face_to_face: 'Yüz Yüze', phone: 'Telefon' };
  const nowMs = Date.now();
  const upcoming = [...appointments]
    .filter((a) => a?.appointmentDate && new Date(a.appointmentDate).getTime() >= nowMs)
    .sort((a, b) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime());
  // No `?? appointments[0]` fallback. `upcoming` is already filtered to future
  // appointments, so falling back to the first of ALL appointments surfaced a
  // past one as "next" — this dashboard was showing a 20 Nis 2026 appointment
  // months later. When nothing is upcoming the card must say "Randevu yok",
  // which it already renders for the null case.
  const nextAppt: any = upcoming[0] ?? null;

  const odevler = assignments.slice(0, 5).map((s: any, i: number) => ({
    id: String(s.id),
    title: s.assignment?.title || 'Ödev',
    status: s.status === 'pending' ? ('pending' as const) : ('completed' as const),
    iconColor: COLORS[i % COLORS.length],
  }));

  // Current week days
  const today = new Date();
  const dow = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - dow + i);
    return {
      day: d.toLocaleDateString('tr-TR', { month: 'long' }),
      date: d.getDate().toString(),
    };
  });

  const displayChartData =
    chartData.length > 0
      ? chartData
      : Array.from({ length: 7 }, (_, i) => ({ label: `Gün ${i + 1}`, value: 0 }));

  return (
    <div>
      {/* Side-by-side only from xl — below that the fixed-width right column
          would leave the flexible left column less than its min-content width,
          making it overflow underneath (cards painting over each other). */}
      <div className="flex flex-col xl:flex-row gap-5">
        {/* ===== LEFT COLUMN ===== */}
        <div className="flex-1 min-w-0">
          {/* Hikayeler — admin panelden yayınlanan (öğrenci hedefli / herkese
              açık) hikayeler. Bar, feed boşken kendini gizler (null döner);
              bir dönem kaldırılmıştı, rol hedeflemeyle birlikte geri geldi. */}
          <div className="mb-4">
            <StoriesBar />
          </div>
          {/* The one place the dashboard says why class-bound cards are empty, and
              where the student can act: Sınıflarım, to join with a class code. */}
          {awaiting && (
            <div
              role="status"
              onClick={() => navigate('/student/siniflarim')}
              className="mb-4 rounded-[16px] bg-[#eaf1fb] px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-[#dfeaf8] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{AWAITING_TITLE}</p>
                <p style={{ ...F, fontSize: 13, color: '#555' }}>{awaitingClassesText((user as any)?.institution?.name)}</p>
              </div>
              <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#3973bf', whiteSpace: 'nowrap' }}>Sınıflarım</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-stretch gap-4 mb-4">
            <div
              className="bg-[#fafafa] rounded-[24px] p-4 xl:p-5 flex-1 flex flex-col justify-center"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <p style={{ ...F, fontSize: 20, fontWeight: 300, color: '#2b2b2b' }}>Hoş geldin Öğrenci,</p>
                  <p style={{ ...F, fontSize: 28, fontWeight: 500, color: '#2b2b2b' }}>
                    {user?.name}
                  </p>
                </div>
                <img
                  src={user?.avatarUrl || '/a-avatar.png'}
                  alt=""
                  className="w-[80px] h-[80px] xl:w-[113px] xl:h-[113px] rounded-full object-cover flex-shrink-0"
                />
              </div>
              <div className="flex justify-between items-center mb-1">
                <span style={{ ...F, fontSize: 18, fontWeight: 500, color: '#3973bf' }}>
                  Kitap İlerlemen
                </span>
                <span style={{ ...F, fontSize: 18, fontWeight: 500, color: '#3973bf' }}>
                  %{overallBookPct}
                </span>
              </div>
              <div className="w-full h-[10px] xl:h-[13px] bg-[#c5c5c5] rounded-[8px]">
                <div
                  className="h-full bg-[#e5599b] rounded-[8px]"
                  style={{ width: `${overallBookPct}%` }}
                />
              </div>
              <div className="flex justify-between flex-wrap gap-x-4 mt-2">
                <span style={{ ...F, fontSize: 13, fontWeight: 500, color: '#2b2b2b' }}>
                  Atanan Kitap: <strong>{totalBooks}</strong>
                </span>
                <span style={{ ...F, fontSize: 13, fontWeight: 500, color: '#2b2b2b' }}>
                  Tamamlanan: <strong>{completedBooks}</strong>
                </span>
              </div>
            </div>

            {/* Right stack: YKS SAYAÇ + Randevularım */}
            <div className="w-full sm:w-[222px] flex-shrink-0 flex flex-col gap-4">
              <div
                className="rounded-[24px] px-4 py-3 xl:px-5 xl:py-4 text-center text-white"
                style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}
              >
                <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#fafafa' }}>YKS SAYAÇ</p>
                {(() => {
                  const { big, small } = yksCountdownLabel(dashboard?.targetExam?.target_date ?? null);
                  return (
                    <>
                      <p style={{ ...F, fontSize: big.length > 4 ? 24 : 32, fontWeight: 500, color: '#fafafa' }}>{big}</p>
                      <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#fafafa' }}>{small}</p>
                    </>
                  );
                })()}
              </div>
            {/* AP37: bireysel (kurumsuz) öğrencide randevu kartı gizli — randevular kurum danışmanlığına bağlı */}
            {!isIndividual && (
            <div
              onClick={() => navigate('/student/randevularim')}
              className="flex-1 bg-[#fafafa] rounded-[24px] overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div className="bg-[#3973bf] px-4 py-3 text-center">
                <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#fafafa' }}>
                  Randevularım
                </span>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-3 py-3 text-center">
                {nextAppt ? (
                  <>
                    <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf' }}>
                      {APPT_TYPE_LABEL[nextAppt.appointment_type ?? ''] ?? nextAppt.subjects?.name ?? 'Randevu'}
                    </p>
                    {nextAppt.subjects?.name && (
                      <p style={{ ...F, fontSize: 12, fontWeight: 300, color: '#3973bf' }}>
                        {nextAppt.subjects.name}
                      </p>
                    )}
                    <p style={{ ...F, fontSize: 13, fontWeight: 300, color: '#3973bf' }}>
                      {FMT_LABEL[(nextAppt.format ?? nextAppt.meeting_type) ?? ''] ?? ''}
                    </p>
                    <p style={{ ...F, fontSize: 13, fontWeight: 300, color: '#3973bf' }}>
                      {nextAppt.appointmentDate
                        ? new Date(nextAppt.appointmentDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '-'}
                    </p>
                  </>
                ) : (
                  <p style={{ ...F, fontSize: 13, fontWeight: 300, color: '#3973bf' }}>
                    {awaiting ? 'Sınıfa yerleştirildiğinde açılır' : 'Randevu yok'}
                  </p>
                )}
              </div>
              {nextAppt?.appointmentDate && daysUntil(nextAppt.appointmentDate) >= 0 && (
                <div className="px-3 py-2 text-center">
                  {/* Three-way, not two-way. `days > 0 ? "N Gün Sonra" : "Bugün"`
                      sent ANY past date to the literal "Bugün" — this dashboard
                      was presenting a 20 Nis 2026 appointment as today's. Same
                      defect existed on the parent and teacher dashboards. */}
                  <span style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf' }}>
                    {daysUntil(nextAppt.appointmentDate) > 0
                      ? `${daysUntil(nextAppt.appointmentDate)} Gün Sonra`
                      : 'Bugün'}
                  </span>
                </div>
              )}
            </div>
            )}
            </div>
          </div>

          {/* Performance Graph */}
          <div
            className="bg-[#fafafa] rounded-[24px] p-4 xl:p-5"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPeriodMenuOpen((o) => !o)}
                  className="bg-[#fafafa] rounded-[16px] px-4 py-2 flex items-center gap-2 cursor-pointer"
                  style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
                >
                  <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b' }}>{PERIOD_LABELS[chartPeriod]}</span>
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="#2b2b2b"><path d="M1 1l4 4 4-4" /></svg>
                </button>
                {periodMenuOpen && (
                  <div className="absolute left-0 top-[110%] bg-white rounded-[12px] shadow-lg border border-gray-100 z-10 min-w-[140px]">
                    {(['week', 'month', 'all'] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => { setChartPeriod(p); setPeriodMenuOpen(false); }}
                        className={`block w-full text-left px-4 py-2 text-[14px] font-semibold hover:bg-gray-50 ${p === chartPeriod ? 'text-[#3973bf]' : 'text-[#2b2b2b]'}`}
                      >{PERIOD_LABELS[p]}</button>
                    ))}
                  </div>
                )}
              </div>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b' }}>
                Başarı Puanı (0 - 100)
              </span>
            </div>
            <div style={{ height: 280 }}>
              <PerformanceChart
                data={displayChartData}
                color="#3973bf"
                id="stu-dash"
                height={280}
                strokeWidth={5}
                domainMin={chartDomain.min}
                domainMax={chartDomain.max}
              />
            </div>
          </div>
        </div>

        {/* ===== RIGHT COLUMN ===== */}
        <div className="hidden lg:block w-full xl:w-[420px] 2xl:w-[500px] xl:flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>
              {weekDays[0]?.day}
            </h3>
            <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>
              Bu Hafta
            </span>
          </div>
          <div
            className="rounded-[24px] p-2.5 flex items-center gap-1.5 mb-3"
            style={{ background: '#fafafa' }}
          >
            {weekDays.map((d, i) => (
              <div
                key={i}
                onClick={() => navigate('/student/ogrenci-programi')}
                className="flex flex-col items-center justify-center flex-1 cursor-pointer transition-colors"
                style={{
                  height: 75,
                  borderRadius: 20,
                  background: i === dow ? '#fafafa' : '#e5599b',
                  border: i === dow ? '2px solid #e5599b' : 'none',
                }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: 500, color: i === dow ? '#e5599b' : '#fafafa' }}
                >
                  {d.day.slice(0, 3)}
                </span>
                <span
                  style={{ fontSize: 26, fontWeight: 600, color: i === dow ? '#e5599b' : '#fafafa' }}
                >
                  {d.date}
                </span>
              </div>
            ))}
            <button className="flex-shrink-0 w-[28px] flex items-center justify-center">
              <svg width="12" height="16" viewBox="0 0 14 16" fill="#1d1b20">
                <path d="M4 2l6 6-6 6" />
              </svg>
            </button>
          </div>

          <div
            className="bg-[#fafafa] rounded-[24px] p-3 xl:p-4 flex items-center gap-2 mb-2"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <span
              style={{ ...F, fontSize: 16, fontWeight: 600, color: '#3973bf' }}
              className="flex-shrink-0"
            >
              Kitap İlerlemen
            </span>
            <div className="flex-1 h-[8px] bg-[#c5c5c5] rounded-[24px]">
              <div
                className="h-full bg-[#3973bf] rounded-[24px]"
                style={{ width: `${overallBookPct}%` }}
              />
            </div>
            <span style={{ ...F, fontSize: 28, fontWeight: 500, color: '#3973bf' }}>
              %{overallBookPct}
            </span>
          </div>

          {/* Book assignment summary — H-2: total assigned, completed, overall % */}
          <div
            className="bg-[#fafafa] rounded-[24px] p-3 xl:p-4 grid grid-cols-3 gap-2 mb-2 text-center"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div>
              <p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#888' }}>Atanan Kitap</p>
              <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#3973bf' }}>{totalBooks}</p>
            </div>
            <div>
              <p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#888' }}>Tamamlanan</p>
              <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#59e56e' }}>{completedBooks}</p>
            </div>
            <div>
              <p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#888' }}>Genel</p>
              <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#e5599b' }}>%{overallBookPct}</p>
            </div>
          </div>

          <div
            className="bg-[#fafafa] rounded-[24px] p-3 xl:p-4 flex items-center gap-2 mb-3"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div className="flex-1">
              <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#e5599b' }}>
                {overallBookPct >= 80
                  ? 'Harika Gidiyorsun!'
                  : overallBookPct >= 40
                  ? 'İyi Gidiyorsun!'
                  : 'Çalışmaya Devam!'}
              </p>
              <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#2b2b2b' }}>
                {overallBookPct >= 50
                  ? 'Kitaplarını bitirmeye az kaldı!'
                  : 'Atanan kitaplardaki testleri çözmeye devam et!'}
              </p>
            </div>
            <img
              src="/a-digikocemoji.png"
              alt=""
              className="w-[50px] h-[50px] xl:w-[74px] xl:h-[74px] object-contain flex-shrink-0"
            />
          </div>

          {/* Ödevlerim — ödev kartı tasarımıyla, grafik hizasında (AP42: Deneme Sınavları kartı anasayfadan kaldırıldı) */}
          <div className="space-y-3">
            {/* AP61: bireysel öğrencide kurum-bağımlı Ödevlerim kartı yerine
                Çalışma Planım (kendi hedeflerini planlayabilir) gösterilir. */}
            {!isIndividual ? (
            <div
              onClick={() => navigate('/student/odevler')}
              className="bg-[#fafafa] rounded-[12px] p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div
                className="w-[65px] h-[65px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                style={{ background: '#3973bf' }}
              >
                <img
                  src="/a-odevicon.png"
                  alt=""
                  className="w-[45px] h-[36px] object-contain"
                  style={{ filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                  <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>Ödevlerim</p>
                  <div className="w-[23px] h-[23px] rounded-full bg-[#ffda09] flex items-center justify-center flex-shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1e1e1e" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                </div>
                <div className="w-full h-[7px] bg-[#c5c5c5] rounded-[8px] mb-1.5">
                  <div className="h-full bg-[#3973bf] rounded-[8px]" style={{ width: '0%' }} />
                </div>
                <p style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>
                  {odevler.filter((o: any) => o.status === 'pending').length > 0
                    ? `${odevler.filter((o: any) => o.status === 'pending').length} bekleyen ödev`
                    : (awaiting ? AWAITING_TITLE : 'Bekleyen ödev yok')}
                </p>
              </div>
            </div>
            ) : (
            <div
              onClick={() => navigate('/student/ogrenci-programi')}
              className="bg-[#fafafa] rounded-[12px] p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div
                className="w-[65px] h-[65px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                style={{ background: '#6497d2' }}
              >
                <img
                  src="/sidebar-icons/calisma-planim.svg"
                  alt=""
                  className="w-[38px] h-[38px] object-contain"
                  style={{ filter: 'brightness(0) invert(1) drop-shadow(0px 4px 4px rgba(0,0,0,0.25))' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                  <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>Çalışma Planım</p>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#6497d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M7 4l6 6-6 6" />
                  </svg>
                </div>
                <p style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>
                  Haftalık hedeflerini planla ve takip et
                </p>
              </div>
            </div>
            )}

          </div>

        </div>
      </div>

      {/* ===== FULL-WIDTH SECTIONS ===== */}
      {/* Bireysel öğrencide kurum-bağımlı sectionlar gizli (#bireysel) */}
      {!isIndividual && (
      <>
      <h2
        className="cursor-pointer hover:text-[#3973bf] transition-colors mt-4 mb-3"
        onClick={() => navigate('/student/odevler')}
        style={{ ...F, fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}
      >
        Ödevlerim
      </h2>
      {odevler.length === 0 && !loading ? (
        <p style={{ ...F, fontSize: 14, color: '#c5c5c5', marginBottom: 16 }}>
          {awaiting ? AWAITING_ASSIGNMENTS_TEXT : 'Bekleyen ödev yok.'}
        </p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-3 mb-4">
          {odevler.map((a) => (
            <div
              key={a.id}
              onClick={() => navigate('/student/odevler')}
              className="bg-[#fafafa] rounded-[12px] p-3 xl:p-4 flex items-center gap-3 flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow"
              style={{ minWidth: 420, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div
                className="w-[65px] h-[65px] xl:w-[75px] xl:h-[75px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                style={{ background: a.iconColor }}
              >
                <img
                  src="/a-odevicon.png"
                  alt=""
                  className="w-[45px] h-[36px] xl:w-[56px] xl:h-[45px] object-contain"
                  style={{ filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p
                    style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}
                    className="truncate pr-2"
                  >
                    {a.title}
                  </p>
                  {a.status === 'completed' ? (
                    <div className="w-[23px] h-[23px] rounded-full bg-[#12d700] flex items-center justify-center flex-shrink-0">
                      <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                        <path
                          d="M1 4.5L4 7.5L11 1"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-[23px] h-[23px] rounded-full bg-[#ffda09] flex items-center justify-center flex-shrink-0">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1e1e1e"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="w-full h-[7px] bg-[#c5c5c5] rounded-[8px] mb-1.5">
                  <div
                    className="h-full rounded-[8px]"
                    style={{
                      width: a.status === 'completed' ? '100%' : '0%',
                      background: a.status === 'completed' ? '#12d700' : '#3973bf',
                    }}
                  />
                </div>
                <div className="flex gap-5">
                  <span style={{ ...F, fontSize: 12, fontWeight: 700, color: '#2b2b2b' }}>
                    Açılış: Süresiz
                  </span>
                  <span style={{ ...F, fontSize: 12, fontWeight: 700, color: '#2b2b2b' }}>
                    Kapanış: Süresiz
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2
        className="cursor-pointer hover:text-[#3973bf] transition-colors mb-3"
        onClick={() => navigate('/student/kutuphanem')}
        style={{ ...F, fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}
      >
        Son Kullanılanlar
      </h2>
      <div className="flex gap-4 overflow-x-auto pb-3 mb-4">
        {books.slice(0, 5).map((b: any, idx: number) => (
          <div
            key={b.id || idx}
            onClick={() => navigate(`/student/kitap-icerik`, { state: { bookId: b.id } })}
            className="bg-[#fafafa] rounded-[12px] w-[240px] xl:w-[310px] flex-shrink-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div className="h-[110px] xl:h-[169px] overflow-hidden">
              <img
                src={b.cover_url || `/a-book${(idx % 5) + 1}.png`}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-3 xl:p-4">
              <p
                className="line-clamp-2 whitespace-pre-line mb-2"
                style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', height: 42 }}
              >
                {b.display_name ?? b.title}
              </p>
              <div
                className="flex justify-between mb-1"
                style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}
              >
                <span>İlerleme</span>
                <span>%{Math.min(100, b.progress_pct ?? 0)}</span>
              </div>
              <div className="w-full h-[8px] bg-[#c5c5c5] rounded-[8px]">
                <div
                  className="h-full bg-[#3973bf] rounded-[8px] transition-[width] duration-500"
                  style={{ width: `${Math.min(100, b.progress_pct ?? 0)}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2
        className="cursor-pointer hover:text-[#3973bf] transition-colors mb-3"
        onClick={() => navigate('/student/kutuphanem')}
        style={{ ...F, fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}
      >
        Kitaplarım
      </h2>
      <div className="flex gap-4 overflow-x-auto pb-3 mb-4">
        {books.slice(0, 5).map((b: any, idx: number) => (
          <div
            key={b.id || idx}
            onClick={() => navigate(`/student/kitap-icerik`, { state: { bookId: b.id } })}
            className="bg-[#fafafa] rounded-[12px] w-[240px] xl:w-[310px] flex-shrink-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div className="h-[110px] xl:h-[169px] overflow-hidden">
              <img
                src={b.cover_url || `/a-book${(idx % 5) + 1}.png`}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-3 xl:p-4">
              <p
                className="line-clamp-2 whitespace-pre-line mb-2"
                style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', height: 42 }}
              >
                {b.display_name ?? b.title}
              </p>
              <div
                className="flex justify-between mb-1"
                style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}
              >
                <span>İlerleme</span>
                <span>%{Math.min(100, b.progress_pct ?? 0)}</span>
              </div>
              <div className="w-full h-[8px] bg-[#c5c5c5] rounded-[8px]">
                <div
                  className="h-full bg-[#3973bf] rounded-[8px] transition-[width] duration-500"
                  style={{ width: `${Math.min(100, b.progress_pct ?? 0)}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2
        className="cursor-pointer hover:text-[#3973bf] transition-colors mb-3"
        onClick={() => navigate('/student/siniflarim')}
        style={{ ...F, fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}
      >
        Sınıflarım
      </h2>
      <div className="flex gap-4 pb-4 flex-wrap">
        {classes.map((c: any, i: number) => (
          <div
            key={c.id || i}
            onClick={() => navigate('/student/siniflarim')}
            className="bg-[#fafafa] rounded-[20px] w-[200px] xl:w-[229px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <div className="h-[80px] xl:h-[103px] overflow-hidden">
              <img
                src={`/a-sinif-tiling${(i % 4) + 1}.png`}
                alt=""
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="p-3 xl:p-4">
              <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>
                {c.campuses?.institutions?.name || c.name || 'Sınıf'}
              </p>
              <div className="flex justify-between items-center mt-1.5">
                <span style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}>
                  {c.name}
                </span>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#2b2b2b] text-lg"
                >
                  •••
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

    </div>
  );
};

export default StudentDashboard;
